import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import {
  flagGradient,
  GOLD_GRADIENT,
  medalGradient,
  performanceBarGradient,
} from '../data/marketColors';
import type { CreativePerformer, LeaderboardRow } from '../data/marketLeaderboardData';
import {
  barFillPercent,
  formatMarketMillions,
  formatMarketRate,
  formatMarketThousands,
} from '../data/utils';
import { CreativeDetailsLink } from '../shared/CreativeDetailsLink';
import { MediaThumb } from '../shared/MediaThumb';

export const LEADERBOARD_GRID_COLUMNS = '40px 1.5fr 1fr 1fr 1fr 1fr 1.5fr 1.5fr';
export const LEADERBOARD_PRINT_GRID_COLUMNS =
  '36px minmax(0, 0.95fr) minmax(0, 0.75fr) minmax(0, 0.75fr) minmax(0, 0.65fr) minmax(0, 0.6fr) minmax(0, 2.2fr) minmax(0, 2.2fr)';
export const LEADERBOARD_COLUMN_HEADERS = [
  'Rank',
  'Market',
  'Performance',
  'Potential savings',
  'Total spend',
  'WoW',
  'Highest performer',
  'Lowest performer',
];

function PerformerCell({
  performer,
  tone,
  accountIds,
}: {
  performer: CreativePerformer;
  tone: 'up' | 'down';
  accountIds: readonly number[];
}): ReactNode {
  const upliftColor = tone === 'up' ? 'success.main' : 'error.dark';
  const sign = performer.upliftPercent >= 0 ? '+' : '';
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minWidth: 0,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        py: 1,
        px: 2,
        '@media print': { overflow: 'visible', py: 0.75, px: 1 },
      }}
    >
      <MediaThumb
        src={performer.thumbnailUrl ?? null}
        previewUrl={performer.previewUrl ?? null}
        size={{ xs: 48, xl: 100 }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '@media print': {
              whiteSpace: 'normal',
              overflow: 'visible',
              textOverflow: 'clip',
              overflowWrap: 'anywhere',
            },
          }}
        >
          {performer.name}
        </Typography>
        <Typography
          sx={{
            fontSize: 9,
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '@media print': {
              whiteSpace: 'normal',
              overflow: 'visible',
              textOverflow: 'clip',
              overflowWrap: 'anywhere',
            },
          }}
        >
          {`Creative ID: ${performer.creativeId}`}
        </Typography>
        <CreativeDetailsLink creativeId={performer.creativeId} accountIds={accountIds} />
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: upliftColor }}>
          {`${sign}${performer.upliftPercent}% uplift`}
        </Typography>
      </Box>
    </Box>
  );
}

export function MarketLeaderboardRow({
  row,
  maxPerformance,
  medalTier,
  currencySymbol,
  accountIds,
}: {
  row: LeaderboardRow;
  maxPerformance: number;
  medalTier: number;
  currencySymbol: string;
  accountIds: readonly number[];
}): ReactNode {
  const leader = medalTier === 1;
  const medal = medalGradient(medalTier);
  const barGradient = leader ? GOLD_GRADIENT : performanceBarGradient(row.rank - 1);
  const flag = flagGradient(row.colorIndex, LEADERBOARD_COLUMN_HEADERS.length);
  const wow = row.weekOverWeek;
  const wowUp = wow !== null && wow >= 0;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: LEADERBOARD_GRID_COLUMNS,
        gap: 3,
        alignItems: 'center',
        borderTop: '1px solid',
        borderColor: 'divider',
        py: 2,
        px: 5,
        '@media print': {
          gridTemplateColumns: LEADERBOARD_PRINT_GRID_COLUMNS,
          gap: 2,
          breakInside: 'avoid',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 22,
          height: 22,
          borderRadius: 1,
          fontSize: 11,
          fontWeight: 800,
          color: medal ? 'common.white' : 'text.secondary',
          background: medal ? `linear-gradient(135deg, ${medal[0]}, ${medal[1]})` : undefined,
          bgcolor: medal ? undefined : 'background.page',
        }}
      >
        {row.rank}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
        <Box
          component="span"
          sx={{
            flexShrink: 0,
            fontSize: 9,
            fontWeight: 700,
            color: 'common.white',
            borderRadius: 1,
            py: 0.5,
            px: 1,
            background: `linear-gradient(135deg, ${flag[0]}, ${flag[1]})`,
          }}
        >
          {row.flag}
        </Box>
        <Typography
          sx={{
            flex: 1,
            minWidth: 0,
            fontWeight: 600,
            color: 'text.primary',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            '@media print': {
              whiteSpace: 'normal',
              overflow: 'visible',
              textOverflow: 'clip',
              overflowWrap: 'anywhere',
            },
          }}
        >
          {row.market}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ width: 30, fontWeight: 700, color: 'text.primary' }}>
          {row.performanceIndex}
        </Typography>
        <Box
          sx={{
            flex: 1,
            minWidth: 30,
            height: 6,
            borderRadius: 1,
            overflow: 'hidden',
            bgcolor: 'background.page',
          }}
        >
          <Box
            data-test="leaderboard-bar-fill"
            sx={{
              width: `${barFillPercent(row.performanceIndex, maxPerformance)}%`,
              height: '100%',
              borderRadius: 1,
              background: `linear-gradient(90deg, ${barGradient[0]}, ${barGradient[1]})`,
            }}
          />
        </Box>
      </Box>

      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>
          {formatMarketRate(row.savingsRate)}
        </Typography>
        <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
          {formatMarketThousands(row.potentialSavingsK, currencySymbol)}
        </Typography>
      </Box>

      <Box sx={{ textAlign: 'right' }}>
        <Typography sx={{ fontWeight: 700, color: 'text.primary' }}>
          {formatMarketMillions(row.spendMillions, currencySymbol)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.5,
          fontWeight: 700,
          color: wow === null ? 'text.secondary' : wowUp ? 'success.main' : 'error.dark',
        }}
      >
        {wow === null ? (
          '-'
        ) : (
          <>
            {wowUp ? (
              <ArrowUpwardRounded sx={{ fontSize: 13 }} />
            ) : (
              <ArrowDownwardRounded sx={{ fontSize: 13 }} />
            )}
            {`${Math.abs(wow).toFixed(1)}%`}
          </>
        )}
      </Box>

      <PerformerCell performer={row.highestPerformer} tone="up" accountIds={accountIds} />
      <PerformerCell performer={row.lowestPerformer} tone="down" accountIds={accountIds} />
    </Box>
  );
}
